import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ELEMENT_IDENTITY_UNCONFIRMED,
  itemKeyOf,
  type ElementCategory,
  type EvidenceSpan,
  type IdentityCandidate,
  type ItemEvidence,
  type ItemVerb,
  type SceneDetailView,
  type SceneIndexRow,
  type SceneItemView,
  type SceneReviewProgress,
  type TextRange,
} from '@scoutaround/script-breakdown-shared';

import { api, ApiError, type ItemIdentityAnswer } from '../../lib/api.js';
import { useCan, useMembership, useRefusal } from '../../lib/permissions.js';
import { useScriptRun, useTakeawaysRun } from '../../lib/useScriptRun.js';
import { useModalFocus } from '../../lib/useModalFocus.js';
import {
  AnalyseWithAi,
  extractionFailureSentence,
  extractionHasRun,
  parseFailure,
  reviewRunStateOf,
  scenesThatDidNotFit,
  scenesWithoutElements,
  shouldOfferAnalysis,
} from '../AnalyseWithAi.js';
import { BreakdownProgress } from '../BreakdownProgress.js';
import { ElementScopeDialog } from '../ElementScopeDialog.js';
import { Icon } from '../Icon.js';
import { ProducerOverview } from '../production/ProducerOverview.js';
import { ProductionPlanProvider, productionPlanKey, type PlanContextValue } from '../production/context.js';
import { EmptyState } from '../ui/EmptyState.js';
import type { MenuItem } from '../ui/MoreMenu.js';
import { PageHeader } from '../ui/PageHeader.js';
import { StatusPill, type WorkState } from '../ui/StatusPill.js';
import { ThreePaneWorkspace, usePaneLayout, type Pane } from '../ui/ThreePaneWorkspace.js';
import { planNavigateFrom, type ElementsAddress, type ElementsNavigate } from './address.js';
import { SceneBreakdownContext, type SceneBreakdownValue } from './context.js';
import { IdentityDialog } from './IdentityDialog.js';
import { TakeawaysRun } from './KeyTakeawaysCard.js';
import { sceneName } from './labels.js';
import { availablePanelTabs, type ScenePanelContext, type ScenePanelTabId } from './panelTabs.js';
import { ProductionPanel } from './ProductionPanel.js';
import { SceneListPanel } from './SceneListPanel.js';
import { ScreenplayReader, type ReaderFlash } from './ScreenplayReader.js';
import { PREFERENCE_KEYS, parseBoolean, serialiseBoolean, usePreference } from './storage.js';
import type { TagScope } from './TagPopover.js';

import '../../styles/elements.css';

const STATUS_ORDER: Readonly<Record<SceneReviewProgress, number>> = {
  NEEDS_RE_REVIEW: 0,
  REOPENED: 1,
  IN_PROGRESS: 2,
  NOT_STARTED: 3,
  REVIEWED: 4,
};

/** The list's rows, narrowed by the address and sorted as it asks. Pure: no count is made here. */
export function visibleRows(
  rows: readonly SceneIndexRow[],
  address: Pick<ElementsAddress, 'filter' | 'cat' | 'ie' | 'tod' | 'char' | 'sort'>,
  matched: ReadonlySet<string> | null,
): SceneIndexRow[] {
  const kept = rows.filter(
    (r) =>
      (matched === null || matched.has(r.id)) &&
      (address.filter === 'ALL' ||
        (address.filter === 'REVIEWED' && r.progress === 'REVIEWED') ||
        (address.filter === 'TO_REVIEW' && r.progress !== 'REVIEWED') ||
        (address.filter === 'NEEDS_RE_REVIEW' && r.progress === 'NEEDS_RE_REVIEW')) &&
      (address.cat === null || (r.counts.byCategory[address.cat] ?? 0) > 0) &&
      (address.ie === null || r.intExt === address.ie) &&
      (address.tod === null || r.timeOfDay === address.tod) &&
      (address.char === null || r.castNames.includes(address.char)),
  );

  if (address.sort === 'LOCATION') {
    return kept.sort((a, b) => (a.slugline ?? '').localeCompare(b.slugline ?? '') || a.ordinal - b.ordinal);
  }
  if (address.sort === 'STATUS') {
    return kept.sort((a, b) => STATUS_ORDER[a.progress] - STATUS_ORDER[b.progress] || a.ordinal - b.ordinal);
  }

  return kept.sort((a, b) => a.ordinal - b.ordinal);
}

/**
 * WHERE A QUOTE SITS IN THE SCENE ON SCREEN — never an invented offset (rule 3).
 *
 * - Its stored offsets only when the text there IS the quote. A production plan span's offsets are
 *   not promised to index this text (an `EXACT` span is stored in screenplay coordinates), and an
 *   `AMBIGUOUS_MATCH` carries its first occurrence, which is never presented as THE evidence.
 * - Otherwise every place the quote appears: the heading first when the span has no offsets and is
 *   `VERIFIED` (the plan contract's heading-only evidence), else the text, then the heading.
 * - Nowhere: no ranges, and the reader goes to the top of the scene rather than mark a guess.
 */
export function evidenceRangesOf(
  span: EvidenceSpan,
  scene: { readonly text: string | null; readonly slugline: string | null },
): { target: 'TEXT' | 'HEADING'; ranges: TextRange[] } {
  const text = scene.text ?? '';
  const needle = span.text.trim();
  if (
    needle !== '' &&
    span.status !== 'AMBIGUOUS_MATCH' &&
    span.start !== null &&
    span.end !== null &&
    span.end > span.start &&
    text.slice(span.start, span.end).trim() === needle
  ) {
    return { target: 'TEXT', ranges: [{ start: span.start, end: span.end }] };
  }
  const find = (haystack: string): TextRange[] => {
    if (needle === '') return [];
    const found: TextRange[] = [];
    for (let at = haystack.indexOf(needle); at !== -1; at = haystack.indexOf(needle, at + needle.length)) {
      found.push({ start: at, end: at + needle.length });
    }
    return found;
  };
  const inText = find(text);
  const inHeading = find(scene.slugline ?? '');
  const headingOnly = span.start === null && span.end === null && span.status === 'VERIFIED';
  if (headingOnly && inHeading.length > 0) return { target: 'HEADING', ranges: inHeading };
  if (inText.length > 0) return { target: 'TEXT', ranges: inText };

  return { target: 'HEADING', ranges: inHeading };
}

interface FlashRequest {
  readonly sceneId: string;
  readonly nonce: number;
  readonly evidence?: ItemEvidence;
  readonly span?: EvidenceSpan;
}

/** A refusal in words, from the server's own sentence where it has one. */
function refusalText(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    if (error.code === 'DECISION_NOT_APPLICABLE') return 'That no longer applies: the item changed in the meantime. The scene was read again.';
    if (error.code === 'BREAKDOWN_WRITE_CONTENDED') return 'Someone else is changing this breakdown — try again.';
    if (error.code === 'VERSION_CONFLICT' || error.code === 'VERSION_REQUIRED') {
      return 'Somebody changed this item first. Its current state is shown; try again if it still needs it.';
    }
    if (error.status === 409 && error.message.startsWith('Request failed')) {
      return 'Somebody changed this first. The scene was read again; try once more if it still needs it.';
    }
    return error.message;
  }

  return error instanceof Error && error.message !== '' ? error.message : fallback;
}

function candidatesOf(error: unknown): IdentityCandidate[] | null {
  if (!(error instanceof ApiError) || error.status !== 409 || error.code !== ELEMENT_IDENTITY_UNCONFIRMED) return null;
  const candidates = (error.detail as { candidates?: unknown } | null | undefined)?.candidates;

  return Array.isArray(candidates) ? (candidates as IdentityCandidate[]) : [];
}

/**
 * THE SCENE BREAKDOWN WORKSPACE — one screen, three panes (Production Workspace 2.0, design record
 * `docs/superpowers/specs/2026-11-04-workspace-scene-breakdown-design.md`).
 *
 * Left, navigation and progress: every scene with its review progress and, apart from it, how much
 * of it the AI reading examined. Centre, the source of truth: the screenplay with each item marked
 * where its evidence is. Right, the decisions: the scene's sheet, category by category, and — while
 * the production plan is on — its Execution and Resources.
 *
 * EVERY FIGURE IS THE SERVER'S (rule 1). The index (`breakdown-index`) carries the progress, the
 * coverage, the counts and the summary from one pass; the open scene (`…/scenes/:id`) carries its
 * text, items and evidence. Nothing here counts rows to make a figure. A write answers with the new
 * state, which is put into the open scene at once; then both reads are refreshed.
 *
 * THE PAGE HAS ONE RED BUTTON: the paid reading while it is on offer (exactly as before), else
 * "Review next scene", else — once the server says the breakdown is complete — "Back to
 * workflows". Marking a scene reviewed is a plain control: it is the bookmark of the work, not the
 * work.
 */
export function ElementsWorkspace({
  projectId,
  scriptVersionId,
  versionNumber,
  address,
  navigate,
  onBackToWorkflows,
  onOpenLocationRequirement,
}: {
  projectId: string;
  scriptVersionId: string;
  versionNumber: number | null;
  address: ElementsAddress;
  navigate: ElementsNavigate;
  /** Back to the choice of workflows; absent where there is none (the Script tab). */
  onBackToWorkflows?: () => void;
  /** Opens a location need of the production plan in the Locations workflow. */
  onOpenLocationRequirement?: (requirementId: string) => void;
}) {
  const queryClient = useQueryClient();
  const features = useMembership()?.features;
  const planOn = features?.productionPlan === true;
  const takeawaysOn = features?.sceneTakeaways === true;
  const refusal = useRefusal('SCRIPT_REVIEW');
  // The reads need READ_SCRIPT; a role without it is told so in the workspace's own words.
  const readRefusal = useRefusal('READ_SCRIPT');
  const readError = useCallback(
    (cause: unknown, fallback: string) =>
      cause instanceof ApiError && cause.status === 403 ? (readRefusal ?? cause.message) : refusalText(cause, fallback),
    [readRefusal],
  );
  const layout = usePaneLayout();

  const addressRef = useRef(address);
  addressRef.current = address;

  const indexKey = useMemo(() => ['breakdown-index', projectId, scriptVersionId] as const, [projectId, scriptVersionId]);
  const scenesKey = useMemo(() => ['breakdown-scene', projectId, scriptVersionId] as const, [projectId, scriptVersionId]);
  const sceneKey = useCallback((sceneId: string) => [...scenesKey, sceneId] as const, [scenesKey]);
  const planKey = useMemo(() => productionPlanKey(projectId, scriptVersionId), [projectId, scriptVersionId]);

  const index = useQuery({
    queryKey: indexKey,
    queryFn: () => api.breakdownIndex(projectId, scriptVersionId),
    staleTime: 30_000,
  });
  const setup = useQuery({ queryKey: ['project-setup', projectId], queryFn: () => api.projectSetup(projectId) });
  const requirements = useQuery({ queryKey: ['requirements', projectId], queryFn: () => api.listRequirements(projectId) });
  const productionPlan = useQuery({
    queryKey: planKey,
    queryFn: () => api.productionPlan(projectId, scriptVersionId),
    enabled: planOn,
  });

  /** Everything a write or a finished reading can change. */
  const refresh = useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: indexKey }),
        queryClient.invalidateQueries({ queryKey: scenesKey }),
        queryClient.invalidateQueries({ queryKey: ['project-setup', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['review-view', scriptVersionId] }),
        // A confirmed or removed CAST element is a Day Out of Days row: a schedule open elsewhere
        // re-reads it (the old breakdown panel did, and the schedule must not go stale).
        queryClient.invalidateQueries({ queryKey: ['dood', projectId] }),
        ...(planOn ? [queryClient.invalidateQueries({ queryKey: planKey })] : []),
      ]),
    [queryClient, indexKey, scenesKey, projectId, scriptVersionId, planOn, planKey],
  );

  // THE SCREEN OWNS ITS OWN RE-READ: when the reading ends, the answer on screen predates it.
  const [rereading, setRereading] = useState(false);
  const { progress, inFlight } = useScriptRun(scriptVersionId, {
    onFinished: () => {
      setRereading(true);
      void Promise.all([refresh(), queryClient.invalidateQueries({ queryKey: ['requirements', projectId] })]).finally(() => {
        setRereading(false);
      });
    },
  });
  const reading = inFlight || rereading;

  // KEY TAKEAWAYS HAVE THEIR OWN RUN, AND THEIR OWN POLL. When one ends, its scenes' readings are
  // new: re-read like after a breakdown. While one runs, no other reading of the draft is offered
  // (the server refuses it: one analysis at a time per draft), but the breakdown never reads as running.
  const takeawaysRun = useTakeawaysRun(scriptVersionId, {
    onFinished: () => {
      void refresh();
    },
  });
  const analysing = reading || takeawaysRun.inFlight;

  // ---- the search: typed here, written to the address once typing pauses, answered by the server ----

  const [query, setQuery] = useState(address.q);
  const written = useRef(address.q);
  useEffect(() => {
    if (address.q !== written.current) {
      written.current = address.q;
      setQuery(address.q);
    }
  }, [address.q]);
  useEffect(() => {
    if (query === address.q) return;
    const timer = window.setTimeout(() => {
      written.current = query;
      navigate({ q: query });
    }, 250);

    return () => {
      window.clearTimeout(timer);
    };
  }, [query, address.q, navigate]);

  const q = address.q.trim();
  const search = useQuery({
    queryKey: ['breakdown-search', projectId, scriptVersionId, q],
    queryFn: () => api.breakdownSearch(projectId, scriptVersionId, q),
    enabled: q !== '',
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

  const rows = useMemo(() => index.data?.rows ?? [], [index.data]);
  const matched = useMemo(
    () => (q === '' || search.data === undefined ? null : new Set(search.data.sceneIds)),
    [q, search.data],
  );
  const visible = useMemo(() => visibleRows(rows, address, matched), [rows, address, matched]);

  // ---- the open scene ----

  const fallbackScene = visible.find((r) => r.progress !== 'REVIEWED') ?? visible[0] ?? rows[0] ?? null;
  const selectedId =
    address.scene !== null && rows.some((r) => r.id === address.scene) ? address.scene : (fallbackScene?.id ?? null);
  const row = rows.find((r) => r.id === selectedId) ?? null;

  const detail = useQuery({
    queryKey: sceneKey(selectedId ?? ''),
    queryFn: () => api.sceneDetail(projectId, scriptVersionId, selectedId as string),
    enabled: selectedId !== null,
    staleTime: 30_000,
  });

  const at = visible.findIndex((r) => r.id === selectedId);
  const prev = at > 0 ? (visible[at - 1] ?? null) : null;
  const next = at === -1 ? (visible[0] ?? null) : (visible[at + 1] ?? null);

  // The neighbours are read before they are asked for, so stepping through scenes is instant.
  useEffect(() => {
    for (const neighbour of [prev, next]) {
      if (neighbour === null) continue;
      void queryClient.prefetchQuery({
        queryKey: sceneKey(neighbour.id),
        queryFn: () => api.sceneDetail(projectId, scriptVersionId, neighbour.id),
        staleTime: 30_000,
      });
    }
  }, [prev, next, queryClient, sceneKey, projectId, scriptVersionId]);

  // ---- layout ----

  const [stackedPane, setStackedPane] = useState<Pane>(address.scene === null ? 'left' : 'center');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [focus, setFocus] = useState(false);
  const [listCollapsed, setListCollapsed] = usePreference(
    PREFERENCE_KEYS.listCollapsed,
    false,
    parseBoolean,
    serialiseBoolean,
  );

  const selectScene = useCallback(
    (sceneId: string) => {
      navigate({
        scene: sceneId,
        item: null,
        req: null,
        ...(addressRef.current.view === 'plan' ? { view: 'breakdown' as const } : {}),
      });
      setDrawerOpen(false);
      if (layout === 'stacked') setStackedPane('center');
    },
    [navigate, layout],
  );

  const selectItem = useCallback(
    (key: string | null) => {
      navigate({ item: key, ...(key !== null && addressRef.current.view !== 'breakdown' ? { view: 'breakdown' as const } : {}) });
    },
    [navigate],
  );

  // ---- showing a place in the script ----

  const [flashRequest, setFlashRequest] = useState<FlashRequest | null>(null);
  const nonce = useRef(0);
  const requestFlash = useCallback(
    (input: Omit<FlashRequest, 'nonce'>) => {
      nonce.current += 1;
      setFlashRequest({ ...input, nonce: nonce.current });
      if (layout === 'stacked') setStackedPane('center');
      if (focus) setFocus(false);
    },
    [layout, focus],
  );
  const flash: ReaderFlash | null = useMemo(() => {
    const data = detail.data;
    if (flashRequest === null || data === undefined || data.scene.id !== flashRequest.sceneId) return null;
    if (flashRequest.evidence !== undefined) {
      return { target: flashRequest.evidence.target, ranges: flashRequest.evidence.ranges, nonce: flashRequest.nonce };
    }
    if (flashRequest.span !== undefined) return { ...evidenceRangesOf(flashRequest.span, data.scene), nonce: flashRequest.nonce };

    return null;
  }, [flashRequest, detail.data]);

  const showItem = useCallback(
    (item: SceneItemView) => {
      if (selectedId === null) return;
      navigate({ item: item.key });
      requestFlash({ sceneId: selectedId, evidence: item.evidence });
    },
    [selectedId, navigate, requestFlash],
  );
  const showRanges = useCallback(
    (evidence: ItemEvidence) => {
      if (selectedId !== null) requestFlash({ sceneId: selectedId, evidence });
    },
    [selectedId, requestFlash],
  );
  const showEvidence = useCallback(
    (span: EvidenceSpan) => {
      if (span.sceneId !== selectedId && rows.some((r) => r.id === span.sceneId)) {
        navigate({ scene: span.sceneId, item: null });
      }
      requestFlash({ sceneId: span.sceneId, span });
    },
    [selectedId, rows, navigate, requestFlash],
  );

  // ---- the writes ----

  const [error, setError] = useState<string | null>(null);
  const [identity, setIdentity] = useState<{
    sceneId: string;
    category: ElementCategory;
    name: string;
    candidates: IdentityCandidate[];
  } | null>(null);
  const [scope, setScope] = useState<{ name: string; category: ElementCategory; sourceSceneId: string; sceneIds: string[] } | null>(
    null,
  );
  const [takeawaysFor, setTakeawaysFor] = useState<readonly string[] | null>(null);

  const failed = useCallback(
    (fallback: string) => (cause: unknown) => {
      setError(refusalText(cause, fallback));
      void refresh();
    },
    [refresh],
  );

  const decide = useMutation({
    mutationFn: (input: { item: SceneItemView; verb: ItemVerb; sceneId: string }) => {
      if (input.item.mentionId === null) throw new Error('Only something a reading proposed here can be decided this way.');

      return api.decideItem(projectId, input.item.mentionId, input.verb);
    },
    onMutate: () => {
      setError(null);
    },
    onSuccess: (result, input) => {
      const key =
        result.productionElementId === null
          ? input.item.key
          : itemKeyOf({ productionElementId: result.productionElementId, elementId: input.item.elementId });
      // The server's answer goes on screen at once; the reads catch up behind it.
      queryClient.setQueryData<SceneDetailView>(sceneKey(input.sceneId), (was) =>
        was === undefined
          ? was
          : {
              ...was,
              items: was.items.map((i) =>
                i.key === input.item.key
                  ? {
                      ...i,
                      key,
                      state: result.state,
                      productionElementId: result.productionElementId ?? i.productionElementId,
                      rowVersion: result.rowVersion,
                    }
                  : i,
              ),
            },
      );
      if (addressRef.current.item === input.item.key && key !== input.item.key) navigate({ item: key });
      void refresh();
    },
    onError: failed('That decision could not be saved.'),
  });

  const rename = useMutation({
    mutationFn: (input: { item: SceneItemView; name: string }) => {
      if (input.item.productionElementId === null || input.item.rowVersion === null) {
        throw new Error('Accept it first: only an item on the sheet has a name to change.');
      }

      return api.renameElement(projectId, input.item.productionElementId, input.name, input.item.rowVersion);
    },
    onMutate: () => {
      setError(null);
    },
    onSuccess: (result, input) => {
      queryClient.setQueriesData<SceneDetailView>({ queryKey: scenesKey }, (was) =>
        was === undefined
          ? was
          : {
              ...was,
              items: was.items.map((i) =>
                i.productionElementId !== null && i.productionElementId === input.item.productionElementId
                  ? { ...i, name: input.name, rowVersion: result.rowVersion }
                  : i,
              ),
            },
      );
      void refresh();
    },
    onError: (cause) => {
      setError(
        cause instanceof ApiError && cause.status === 409
          ? 'Somebody changed this item first. Its current name is shown; rename it again if it still needs it.'
          : refusalText(cause, 'The new name could not be saved.'),
      );
      void refresh();
    },
  });

  const count = useMutation({
    mutationFn: async (input: { item: SceneItemView; quantity: number | null; sceneId: string }) => {
      let id = input.item.productionElementId;
      // A count is a judgement, and making one puts the item on the sheet first.
      if (id === null) {
        if (input.item.mentionId === null) throw new Error('That item could not be put on the sheet.');
        id = (await api.decideItem(projectId, input.item.mentionId, 'ACCEPT')).productionElementId;
      }
      if (id === null) throw new Error('That item could not be put on the sheet.');

      return api.setElementQuantity(projectId, input.sceneId, id, input.quantity);
    },
    onMutate: () => {
      setError(null);
    },
    onSuccess: () => {
      void refresh();
    },
    onError: failed('The count could not be saved.'),
  });

  const removeItem = useMutation({
    mutationFn: (input: { productionElementId: string; sceneId: string }) =>
      api.removeSceneElement(projectId, input.sceneId, input.productionElementId),
    onMutate: () => {
      setError(null);
    },
    onSuccess: () => {
      void refresh();
    },
    onError: failed('It could not be taken off this scene.'),
  });

  const addItem = useMutation({
    mutationFn: (input: { sceneId: string; category: ElementCategory; name: string; identity?: ItemIdentityAnswer }) =>
      api.addSceneItem(projectId, input.sceneId, { category: input.category, name: input.name, identity: input.identity }),
    onMutate: () => {
      setError(null);
    },
    onSuccess: () => {
      setIdentity(null);
      void refresh();
    },
    onError: (cause, input) => {
      const candidates = candidatesOf(cause);
      if (candidates !== null) {
        // Not a failure: the server asks whether this is the same item before joining them.
        setIdentity({ sceneId: input.sceneId, category: input.category, name: input.name, candidates });
        return;
      }
      if (identity === null) setError(refusalText(cause, 'That could not be added.'));
    },
  });

  const addEverywhere = useMutation({
    mutationFn: (input: { category: ElementCategory; name: string; sceneIds: readonly string[] }) =>
      api.addElementToScenes(projectId, input),
    onSuccess: () => {
      setScope(null);
      void refresh();
    },
  });

  /**
   * OFF EVERY SCENE IT IS ON, ASKED FIRST — the list is the server's `onScenes` for the item (the
   * live scenes of this draft whose sheet carries it), shown in the scope dialog and sent exactly as
   * shown. The server records each removal as the per-scene state machine does; nothing else moves.
   */
  const [removal, setRemoval] = useState<{ item: SceneItemView; sourceSceneId: string } | null>(null);
  const removeAll = useMutation({
    mutationFn: (input: { productionElementId: string; sceneIds: readonly string[] }) =>
      api.removeElementFromScenes(projectId, input.productionElementId, input.sceneIds),
    onSuccess: () => {
      setRemoval(null);
      void refresh();
    },
    onError: () => {
      // The dialog says why; what is on the sheet now is read again behind it.
      void refresh();
    },
  });

  /** "Read this scene again with AI": the open scene's paid reading, in its own dialog. */
  const canRunAi = useCan('AI_RUN');
  const [rereadFor, setRereadFor] = useState<string | null>(null);

  const review = useMutation({
    mutationFn: (input: { sceneId: string; reviewed: boolean }) =>
      input.reviewed ? api.markSceneReviewed(projectId, input.sceneId) : api.unmarkSceneReviewed(projectId, input.sceneId),
    onMutate: () => {
      setError(null);
    },
    onSuccess: () => {
      void refresh();
    },
    onError: failed('The scene’s review could not be saved.'),
  });

  const busy =
    decide.isPending ||
    rename.isPending ||
    count.isPending ||
    removeItem.isPending ||
    addItem.isPending ||
    addEverywhere.isPending ||
    removeAll.isPending ||
    review.isPending;

  /**
   * Tag words from the script: this scene, or every scene the server finds them in, asked first.
   *
   * "Every scene that names it" is a WHOLE-WORD match (`match=word`, `appearsInText`'s rule), never
   * the list box's substring: the confirmed list is what gets written, and "car" must not land on
   * the scene with a scarf in it. Its own cache key, so the list's substring answer is never reused.
   */
  const tag = useCallback(
    async (text: string, category: ElementCategory, where: TagScope) => {
      if (selectedId === null) return;
      if (where === 'SCENE') {
        addItem.mutate({ sceneId: selectedId, category, name: text });
        return;
      }
      let found: string[];
      try {
        found = (
          await queryClient.fetchQuery({
            queryKey: ['breakdown-search', projectId, scriptVersionId, text.trim(), 'word'],
            queryFn: () => api.breakdownSearch(projectId, scriptVersionId, text.trim(), 'word'),
            staleTime: 60_000,
          })
        ).sceneIds;
      } catch (cause) {
        setError(refusalText(cause, 'The scenes that name it could not be found. Tag this scene alone, or try again.'));
        return;
      }
      const wanted = new Set([selectedId, ...found]);
      const sceneIds = rows.filter((r) => wanted.has(r.id)).map((r) => r.id);
      if (sceneIds.length <= 1) {
        addItem.mutate({ sceneId: selectedId, category, name: text });
        return;
      }
      addEverywhere.reset();
      setScope({ name: text, category, sourceSceneId: selectedId, sceneIds });
    },
    [selectedId, addItem, addEverywhere, queryClient, projectId, scriptVersionId, rows],
  );

  // ---- the header: where the work stands, and the one thing to do next ----

  const summary = index.data?.summary ?? null;
  const runState = reviewRunStateOf(progress, reading);
  const didNotFit = scenesThatDidNotFit(progress);
  const withoutElements = scenesWithoutElements(progress);
  const offersAnalysis =
    !analysing &&
    shouldOfferAnalysis({
      requirementCount: extractionHasRun(progress) ? 1 : 0,
      scenesDidNotFit: didNotFit,
      scenesWithoutElements: withoutElements,
      lastRunFailed: progress?.status === 'FAILED' && parseFailure(progress) === null,
    });
  const complete = setup.data?.workflows.breakdown === 'COMPLETED';

  /** The next scene in the list's order that is not reviewed, after the open one (wrapping), else anywhere. */
  const nextToReview = useMemo(() => {
    const order = visible;
    const from = order.findIndex((r) => r.id === selectedId);
    const pick = (list: readonly SceneIndexRow[]) => list.find((r) => r.progress !== 'REVIEWED' && r.id !== selectedId);
    const inList = pick(order.slice(from + 1)) ?? pick(order.slice(0, Math.max(0, from)));
    if (inList !== undefined) return { id: inList.id, widen: false };
    const anywhere = pick(rows);

    return anywhere === undefined ? null : { id: anywhere.id, widen: true };
  }, [visible, rows, selectedId]);

  const reviewNext = () => {
    if (nextToReview === null) return;
    if (nextToReview.widen) navigate({ filter: 'ALL', q: '', cat: null, ie: null, tod: null, char: null });
    selectScene(nextToReview.id);
  };

  const primary: 'ANALYSE' | 'NEXT' | 'BACK' | null = offersAnalysis
    ? 'ANALYSE'
    : complete
      ? onBackToWorkflows === undefined
        ? null
        : 'BACK'
      : nextToReview !== null
        ? 'NEXT'
        : null;

  const pill: { state: WorkState; label?: string } | null =
    runState === 'PROCESSING'
      ? { state: 'PROCESSING' }
      : runState === 'FAILED'
        ? { state: 'FAILED', label: 'Breakdown failed' }
        : runState === 'PARTIAL'
          ? { state: 'NEEDS_ATTENTION', label: 'Partly read' }
          : setup.data === undefined
            ? null
            : setup.data.workflows.breakdown === 'COMPLETED'
              ? { state: 'COMPLETED', label: 'Reviewed' }
              : setup.data.workflows.breakdown === 'IN_PROGRESS'
                ? { state: 'IN_PROGRESS' }
                : runState === 'NOT_STARTED'
                  ? { state: 'NOT_STARTED' }
                  : { state: 'READY_FOR_REVIEW' };

  const lede =
    runState === 'PROCESSING'
      ? 'The AI breakdown is running. Everything it finds arrives together when it finishes — you can leave this page.'
      : runState === 'FAILED'
        ? extractionFailureSentence(progress)
        : runState === 'PARTIAL'
          ? didNotFit > 0
            ? `${String(didNotFit)} ${didNotFit === 1 ? 'scene was' : 'scenes were'} not read — the screenplay is longer than one request can carry. What was found stays as it is.`
            : `${String(withoutElements)} ${withoutElements === 1 ? 'scene was' : 'scenes were'} not fully read — the AI got no answer for some departments. Their empty categories say so; tag what is missing by hand.`
          : runState === 'NOT_STARTED'
            ? 'Tag what each scene needs by selecting words in the script, then mark the scene reviewed.'
            : 'Accept what the AI read or mark it not needed, tag what it missed, then mark the scene reviewed.';

  // ---- the production plan, while it is on ----

  const sceneLabels = useMemo(
    () => new Map(rows.map((r) => [r.id, `${sceneName(r)}${r.slugline === null ? '' : ` · ${r.slugline}`}`])),
    [rows],
  );
  const planNavigate = useMemo(() => planNavigateFrom(navigate), [navigate]);
  const planContext: PlanContextValue | null = useMemo(() => {
    if (!planOn) return null;
    const cause = productionPlan.error;

    return {
      projectId,
      scriptVersionId,
      plan: productionPlan.data ?? null,
      loading: productionPlan.isPending,
      error:
        cause === null
          ? null
          : cause instanceof ApiError && cause.code === 'PRODUCTION_PLAN_OFF'
            ? 'The production plan is switched off for now.'
            : cause.message !== ''
              ? cause.message
              : 'The production plan could not be loaded.',
      sceneLabel: (sceneId: string) => sceneLabels.get(sceneId) ?? 'A scene not in this draft',
      navigate: planNavigate,
      focusRequirementId: address.req,
      onOpenLocationRequirement,
      // The plan's quotes drive the reader beside them; the full-width overview has no reader.
      ...(address.view === 'plan' ? {} : { showEvidence }),
    };
  }, [
    planOn,
    productionPlan.data,
    productionPlan.isPending,
    productionPlan.error,
    projectId,
    scriptVersionId,
    sceneLabels,
    planNavigate,
    address.req,
    address.view,
    onOpenLocationRequirement,
    showEvidence,
  ]);

  // ---- the panes' shared value ----

  const value: SceneBreakdownValue = {
    projectId,
    scriptVersionId,
    row,
    detail: detail.data ?? null,
    detailLoading: detail.isPending && selectedId !== null,
    detailError: detail.error === null ? null : readError(detail.error, 'This scene could not be loaded.'),
    retryDetail: () => {
      void detail.refetch();
    },
    selectedItem: address.item,
    selectItem,
    showItem,
    showEvidence,
    showRanges,
    decide: (item, verb) => {
      if (selectedId !== null) decide.mutate({ item, verb, sceneId: selectedId });
    },
    rename: (item, name) => {
      rename.mutate({ item, name });
    },
    setCount: (item, quantity) => {
      if (selectedId !== null) count.mutate({ item, quantity, sceneId: selectedId });
    },
    remove: (item) => {
      if (selectedId !== null && item.productionElementId !== null) {
        removeItem.mutate({ productionElementId: item.productionElementId, sceneId: selectedId });
      }
    },
    removeEverywhere: (item) => {
      if (selectedId !== null && item.productionElementId !== null && (item.onScenes?.length ?? 0) > 1) {
        removeAll.reset();
        setRemoval({ item, sourceSceneId: selectedId });
      }
    },
    // Hidden while a reading runs (it would buy the scene twice) and from a role that does not run AI.
    readScene:
      analysing || !canRunAi
        ? null
        : () => {
            if (selectedId !== null) setRereadFor(selectedId);
          },
    add: (category, name) => {
      if (selectedId !== null) addItem.mutate({ sceneId: selectedId, category, name });
    },
    markReviewed: () => {
      if (selectedId !== null) review.mutate({ sceneId: selectedId, reviewed: true });
    },
    unmarkReviewed: () => {
      if (selectedId !== null) review.mutate({ sceneId: selectedId, reviewed: false });
    },
    // Key Takeaways for the open scene were started: their own poll picks the run up.
    onReadingStarted: () => {
      void queryClient.invalidateQueries({ queryKey: ['takeaways-progress', scriptVersionId] });
    },
    busy,
    error,
    clearError: () => {
      setError(null);
    },
    refusal,
    planOn,
    takeawaysOn,
    requirements: {
      data: requirements.data,
      error: requirements.error,
      isPending: requirements.isPending,
      refetch: requirements.refetch,
    },
  };

  const tabs = availablePanelTabs(features);
  const view: ScenePanelTabId = tabs.some((t) => t.id === address.view) ? (address.view as ScenePanelTabId) : 'breakdown';
  const panelCtx: ScenePanelContext | null =
    selectedId === null
      ? null
      : { projectId, scriptVersionId, sceneId: selectedId, selectedItem: address.item, selectItem, showEvidence };

  const listMenu: MenuItem[] =
    takeawaysOn && visible.length > 0
      ? [
          {
            id: 'takeaways',
            label: `Key takeaways for the ${String(visible.length)} ${visible.length === 1 ? 'scene' : 'scenes'} shown`,
            hint: 'A paid reading of their breakdown, with its own estimate',
            testId: 'el-list-takeaways',
            onSelect: () => {
              setTakeawaysFor(visible.map((r) => r.id));
            },
          },
        ]
      : [];

  const header = (
    <div className="el-head">
      <PageHeader
        testId="elements-head"
        eyebrow={`Detailed breakdown${versionNumber === null ? '' : ` · Script v${String(versionNumber)}`}`}
        title={
          <span data-testid="elements-headline">
            {summary === null
              ? 'Scene breakdown'
              : `${String(summary.reviewed)} of ${String(summary.scenes)} ${summary.scenes === 1 ? 'scene' : 'scenes'} reviewed`}
          </span>
        }
        status={pill === null ? undefined : <StatusPill state={pill.state} label={pill.label} testId="elements-state" />}
        lede={<span data-testid="elements-lede">{lede}</span>}
        actions={
          <>
            {complete || nextToReview === null ? null : (
              <button
                type="button"
                className={primary === 'NEXT' ? 'btn btn-primary' : 'btn btn-ghost'}
                data-testid="elements-review-next"
                disabled={busy}
                onClick={reviewNext}
              >
                Review next scene
                <Icon name="arrowRight" size={16} />
              </button>
            )}
            {onBackToWorkflows === undefined ? null : (
              <button
                type="button"
                className={primary === 'BACK' ? 'btn btn-primary' : 'btn btn-ghost'}
                data-testid="elements-back-to-workflows"
                disabled={busy}
                onClick={onBackToWorkflows}
              >
                Back to workflows
                {primary === 'BACK' ? <Icon name="arrowRight" size={16} /> : null}
              </button>
            )}
          </>
        }
      />

      {reading ? (
        <div className="el-analyse" data-testid="elements-progress">
          <BreakdownProgress scriptVersionId={scriptVersionId} />
        </div>
      ) : takeawaysRun.inFlight ? (
        // Not the breakdown: only the scenes asked for are being read, and nothing on the sheet changes.
        <p className="el-analyse el-analyse-note" role="status" data-testid="elements-takeaways-progress">
          {takeawaysRun.progress?.totalUnits === null || takeawaysRun.progress?.totalUnits === undefined
            ? 'Reading key takeaways…'
            : `Reading key takeaways for ${String(takeawaysRun.progress.totalUnits)} ${takeawaysRun.progress.totalUnits === 1 ? 'scene' : 'scenes'}…`}
        </p>
      ) : offersAnalysis ? (
        <div className="el-analyse" data-testid="elements-analyse">
          <AnalyseWithAi projectId={projectId} versionId={scriptVersionId} sceneCount={rows.length} reads="ELEMENTS" chooseCategories />
        </div>
      ) : null}
    </div>
  );

  let body;
  if (index.isPending) {
    body = (
      <p className="mm el-loading" data-testid="elements-loading">
        Loading the breakdown…
      </p>
    );
  } else if (index.isError) {
    body = (
      <div className="el-error" role="alert" data-testid="elements-load-error">
        <span>{readError(index.error, 'The breakdown could not be loaded.')}</span>
        <button
          type="button"
          className="ctl"
          onClick={() => {
            void index.refetch();
          }}
        >
          Try again
        </button>
      </div>
    );
  } else if (rows.length === 0) {
    body = (
      <EmptyState title="No scenes in this draft" testId="elements-no-scenes">
        The breakdown works scene by scene. Once the screenplay’s scenes are confirmed they appear here.
      </EmptyState>
    );
  } else if (planContext !== null && address.view === 'plan') {
    // The whole screenplay's plan takes the panes' place, full width.
    body = <ProducerOverview />;
  } else {
    body = (
      <ThreePaneWorkspace
        testId="elements-workspace"
        layout={layout}
        labels={{ left: 'Scenes', center: 'Script', right: 'Breakdown' }}
        leftCollapsed={layout === 'three' && listCollapsed}
        focus={focus && layout !== 'stacked'}
        stackedPane={stackedPane}
        onStackedPane={setStackedPane}
        drawerOpen={drawerOpen}
        onDrawerOpen={setDrawerOpen}
        drawerButtonLabel={row === null ? undefined : `${sceneName(row)}${row.slugline === null ? '' : ` · ${row.slugline}`}`}
        left={
          summary === null ? null : (
            <SceneListPanel
              rows={visible}
              allRows={rows}
              summary={summary}
              address={address}
              navigate={navigate}
              selectedId={selectedId}
              onSelect={selectScene}
              query={query}
              onQuery={setQuery}
              searching={q !== '' && search.isFetching && search.data === undefined}
              searchError={search.isError ? readError(search.error, 'The search did not answer; every scene is shown.') : null}
              collapsed={layout === 'three' && listCollapsed}
              onCollapsed={setListCollapsed}
              collapsible={layout === 'three'}
              menu={listMenu}
            />
          )
        }
        center={
          <ScreenplayReader
            versionNumber={versionNumber}
            prevLabel={prev === null ? null : sceneName(prev)}
            nextLabel={next === null ? null : sceneName(next)}
            onPrev={() => {
              if (prev !== null) selectScene(prev.id);
            }}
            onNext={() => {
              if (next !== null) selectScene(next.id);
            }}
            focus={focus}
            onFocus={setFocus}
            flash={flash}
            onPickItem={(key) => {
              selectItem(key);
              if (layout === 'stacked') setStackedPane('right');
            }}
            onTag={(text, category, where) => {
              void tag(text, category, where);
            }}
          />
        }
        right={
          panelCtx === null ? null : (
            <ProductionPanel
              tabs={tabs}
              view={view}
              onView={(nextView) => {
                navigate({ view: nextView, req: null });
              }}
              ctx={panelCtx}
              onWholePlan={
                planOn
                  ? () => {
                      navigate({ view: 'plan', req: null }, { push: true });
                    }
                  : undefined
              }
            />
          )
        }
      />
    );
  }

  const byId = new Map(rows.map((r) => [r.id, r]));
  const suggestion = (() => {
    const cast = row?.castNames[0];
    if (identity === null || cast === undefined) return null;
    const pretty = cast.charAt(0) + cast.slice(1).toLowerCase();

    return `${pretty}’s ${identity.name.toLowerCase()}`;
  })();

  return (
    <SceneBreakdownContext.Provider value={value}>
      <ProductionPlanProvider value={planContext}>
        <section className="el" data-testid="elements-screen" data-primary={primary ?? 'NONE'}>
          {header}
          {body}
        </section>

        {identity === null ? null : (
          <IdentityDialog
            name={identity.name}
            category={identity.category}
            candidates={identity.candidates}
            suggestion={suggestion}
            busy={addItem.isPending}
            error={addItem.isError && candidatesOf(addItem.error) === null ? refusalText(addItem.error, 'That could not be added.') : null}
            onSame={(productionElementId) => {
              addItem.mutate({
                sceneId: identity.sceneId,
                category: identity.category,
                name: identity.name,
                identity: { sameAs: productionElementId },
              });
            }}
            onDistinct={(name) => {
              addItem.mutate({ sceneId: identity.sceneId, category: identity.category, name, identity: { distinct: true } });
            }}
            onCancel={() => {
              setIdentity(null);
            }}
          />
        )}

        {scope === null ? null : (
          <ElementScopeDialog
            mode="TAG"
            name={scope.name}
            sourceSceneId={scope.sourceSceneId}
            scenes={scope.sceneIds.map((id) => ({
              id,
              sceneNumber: byId.get(id)?.sceneNumber ?? null,
              slugline: byId.get(id)?.slugline ?? null,
            }))}
            busy={addEverywhere.isPending}
            error={addEverywhere.error === null ? null : refusalText(addEverywhere.error, 'That could not be saved.')}
            onCancel={() => {
              setScope(null);
            }}
            onThisScene={() => {
              const ask = scope;
              setScope(null);
              addItem.mutate({ sceneId: ask.sourceSceneId, category: ask.category, name: ask.name });
            }}
            onAllScenes={() => {
              addEverywhere.mutate({ category: scope.category, name: scope.name, sceneIds: scope.sceneIds });
            }}
          />
        )}

        {removal === null ? null : (
          <ElementScopeDialog
            mode="REMOVE"
            name={removal.item.name}
            sourceSceneId={removal.sourceSceneId}
            scenes={(removal.item.onScenes ?? []).map((s) => ({
              id: s.sceneId,
              sceneNumber: s.sceneNumber,
              slugline: byId.get(s.sceneId)?.slugline ?? null,
            }))}
            busy={removeAll.isPending}
            error={removeAll.error === null ? null : refusalText(removeAll.error, 'It could not be taken off those scenes.')}
            onCancel={() => {
              setRemoval(null);
            }}
            onThisScene={() => {
              const ask = removal;
              setRemoval(null);
              if (ask.item.productionElementId !== null) {
                removeItem.mutate({ productionElementId: ask.item.productionElementId, sceneId: ask.sourceSceneId });
              }
            }}
            onAllScenes={() => {
              if (removal.item.productionElementId === null) return;
              removeAll.mutate({
                productionElementId: removal.item.productionElementId,
                sceneIds: (removal.item.onScenes ?? []).map((s) => s.sceneId),
              });
            }}
          />
        )}

        {rereadFor === null ? null : (
          <SceneRereadDialog
            projectId={projectId}
            scriptVersionId={scriptVersionId}
            sceneId={rereadFor}
            sceneLabel={(() => {
              const r = byId.get(rereadFor);
              return r === undefined ? 'this scene' : sceneName(r);
            })()}
            onClose={() => {
              setRereadFor(null);
            }}
            onStarted={() => {
              setRereadFor(null);
              void queryClient.invalidateQueries({ queryKey: ['script-progress', scriptVersionId] });
            }}
          />
        )}

        {takeawaysFor === null ? null : (
          <TakeawaysApprovalDialog
            projectId={projectId}
            scriptVersionId={scriptVersionId}
            sceneIds={takeawaysFor}
            sceneLabel={(id) => {
              const r = byId.get(id);
              return r === undefined ? 'A scene' : sceneName(r);
            }}
            onClose={() => {
              setTakeawaysFor(null);
            }}
            onStarted={() => {
              void queryClient.invalidateQueries({ queryKey: ['takeaways-progress', scriptVersionId] });
            }}
          />
        )}
      </ProductionPlanProvider>
    </SceneBreakdownContext.Provider>
  );
}

/**
 * READ ONE SCENE AGAIN — the same paid reading as "Analyse with AI", for one scene.
 *
 * The workspace reads the whole screenplay at once; a scene that was read before it changed, read
 * in part, or not read at all is read again on its own here, through `AnalyseWithAi` with that one
 * scene: its paid-request wording, the estimate and approval while AI is metered, and nothing run
 * until the producer presses its button. In a dialog, so its button is that dialog's one primary
 * and never the page's (`btn-primary` stays one per screen); its controls carry their own test ids.
 * The producer picks which element categories the reading looks for (`chooseCategories`).
 */
function SceneRereadDialog({
  projectId,
  scriptVersionId,
  sceneId,
  sceneLabel,
  onClose,
  onStarted,
}: {
  projectId: string;
  scriptVersionId: string;
  sceneId: string;
  sceneLabel: string;
  onClose: () => void;
  onStarted: () => void;
}) {
  const modal = useModalFocus<HTMLDivElement>(onClose);
  const sceneIds = useMemo(() => [sceneId], [sceneId]);

  return (
    <div
      className="el-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="el-reread-title"
      data-testid="el-reread-dialog"
      onKeyDown={modal.onKeyDown}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="el-modal-box" ref={modal.ref}>
        <header className="el-modal-head">
          <h2 className="el-modal-title" id="el-reread-title">
            {`Read ${sceneLabel} with AI`}
          </h2>
        </header>
        <div className="el-modal-body">
          <p className="el-modal-text">Only this scene is sent to the reading; the rest of the screenplay is not read again.</p>
          <AnalyseWithAi
            projectId={projectId}
            versionId={scriptVersionId}
            sceneCount={1}
            sceneIds={sceneIds}
            reads="ELEMENTS"
            chooseCategories
            testId="el-reread-panel"
            testIdPrefix="el-reread"
            onStarted={onStarted}
          />
        </div>
        <footer className="el-modal-foot">
          <button type="button" className="btn btn-ghost" data-testid="el-reread-close" onClick={onClose}>
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}

/**
 * Key Takeaways for several scenes, in their own dialog (D5): started the way the breakdown is
 * (`TakeawaysRun`) — the estimate and approval while AI is metered, the paid-request note and one
 * confirming click while it is not.
 */
function TakeawaysApprovalDialog({
  projectId,
  scriptVersionId,
  sceneIds,
  sceneLabel,
  onClose,
  onStarted,
}: {
  projectId: string;
  scriptVersionId: string;
  sceneIds: readonly string[];
  sceneLabel: (sceneId: string) => string;
  onClose: () => void;
  onStarted: () => void;
}) {
  const modal = useModalFocus<HTMLDivElement>(onClose);

  return (
    <div
      className="el-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="el-takeaways-many-title"
      data-testid="el-takeaways-many"
      onKeyDown={modal.onKeyDown}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="el-modal-box" ref={modal.ref}>
        <header className="el-modal-head">
          <p className="el-modal-kicker">AI-assisted reading — not a production plan</p>
          <h2 className="el-modal-title" id="el-takeaways-many-title">
            {`Key takeaways for ${String(sceneIds.length)} ${sceneIds.length === 1 ? 'scene' : 'scenes'}`}
          </h2>
        </header>
        <div className="el-modal-body">
          <p className="el-modal-text">
            Read from each scene’s breakdown as it is now. Scenes with nothing on their sheet have nothing to read from.
          </p>
          <TakeawaysRun
            projectId={projectId}
            scriptVersionId={scriptVersionId}
            sceneIds={sceneIds}
            sceneLabel={sceneLabel}
            testId="el-takeaways-many-approval"
            primary
            onClose={onClose}
            onStarted={({ legacy }) => {
              onStarted();
              // Unmetered, the start is all there is to show; the metered approval says it started.
              if (legacy) onClose();
            }}
          />
        </div>
        <footer className="el-modal-foot">
          <button type="button" className="btn btn-ghost" data-testid="el-takeaways-many-close" onClick={onClose}>
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}
